import React, { useMemo } from 'react'

import { Button, Grid, Typography, Box } from '@barracuda-internal/bds-core'

import InboundMachineLearning from 'components/pages/support/help/InboundMachineLearning/InboundMachineLearning'
import inboundSettingsStyles from 'components/pages/inboundSettings/inboundSettingsStyles'
import { useFormatMessage } from 'lib/localization'
import { PageTitle, FormSpacer } from 'components/libs/layout/pages/pageLayout'
import { useMachineLearningLogic } from 'components/pages/inboundSettings/machineLearning/useMachineLearningLogic'
import { SettingsPageContainer } from 'components/libs/layout/pages/SettingsPageContainer'
import { GROUP_TITLE, TOPICS } from 'components/pages/support/config'
import DomainTable from 'components/libs/domainTable/DomainTable'
import { useIsReadonly } from 'lib/hooks/useIsReadonly'
import NoDomainPolicyAccessAlert from 'components/libs/layout/pages/NoDomainPolicyAccessAlert'

const BASE_I18N_KEY = 'ess.inbound_settings.machine_learning'

const MachineLearning: React.FC = () => {
  const isReadonly = useIsReadonly()
  const inboundClasses = inboundSettingsStyles()
  const formatMessage = useFormatMessage(BASE_I18N_KEY)

  const [state, eventHandlers] = useMachineLearningLogic()

  return useMemo(
    () => (
      <SettingsPageContainer dirtyCheck isDirtyForm={state.hasPageChanges}>
        <PageTitle
          title={formatMessage('title')}
          onOpenSupport={eventHandlers.helpConfig.onHelpClick}
          help={{
            content: InboundMachineLearning,
            onClose: eventHandlers.helpConfig.onCloseHelp,
            title: TOPICS.machineLearning,
            group: GROUP_TITLE.inboundSettings,
            isHelpOpen: eventHandlers.helpConfig.isOpen
          }}
        />
        {state.hasNoDomainPolicyAccess && <NoDomainPolicyAccessAlert />}
        {!state.hasNoDomainPolicyAccess && (
          <Box>
            <FormSpacer />
            <Grid container>
              <Grid container className={inboundClasses.spaceGrid}>
                <Typography variant="h4">{formatMessage('machine_learning_exempt')}</Typography>
                <Button
                  className={inboundClasses.bulkEdit}
                  onClick={eventHandlers.onBulkEdit}
                  variant="contained"
                  size="small"
                  data-testid="bulk-button"
                  disabled={isReadonly}
                >
                  Bulk Edit
                </Button>
              </Grid>
              <DomainTable
                data={state.tableData}
                onAddItem={eventHandlers.onAddItem}
                onRemoveItem={eventHandlers.onRemoveItem}
                shouldHandleEmail
                data-testid="machine-learning-exempt-table"
                delegateDirtyFormState={eventHandlers.setIsMachineLearningTableDirty}
              />
            </Grid>
          </Box>
        )}
      </SettingsPageContainer>
    ),
    [
      state.hasPageChanges,
      state.hasNoDomainPolicyAccess,
      state.tableData,
      formatMessage,
      eventHandlers.helpConfig.onHelpClick,
      eventHandlers.helpConfig.onCloseHelp,
      eventHandlers.helpConfig.isOpen,
      eventHandlers.onBulkEdit,
      eventHandlers.onAddItem,
      eventHandlers.onRemoveItem,
      eventHandlers.setIsMachineLearningTableDirty,
      inboundClasses.spaceGrid,
      inboundClasses.bulkEdit,
      isReadonly
    ]
  )
}

export default MachineLearning
